import React, { useContext, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Flex, InviteContent, Note } from "../../styles";
import { Input } from "../../../../../components/common/Input";
import { Button } from "../../../../../components/common/Button";
import { InputPhone } from "../../../../../components/common/InputPhone";
import CustomModal from "../../../../../components/common/CustomModal";
import CustomSelect from "../../../../../components/common/CustomSelect";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import { GetAllRoles } from "../../redux/actions";
import { NetworkErrorAlertContext } from "../../../../../context/NetworkErrorAlert";
import { capitalizeName } from "../../utils/capitalizeName";

interface Props {
    setState?: (data) => void;
    open: boolean;
    handleInviteTeam?: (data) => void;
    apiSuccess: boolean;
}

export interface formData {
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber?: string;
    role?:string;
}

const InviteModal = ({
    setState,
    apiSuccess,
    open,
    handleInviteTeam,
}: Props) => {
    const {
        handleSubmit,
        control,
        setValue,
        formState: { errors },
    } = useForm<formData>() || {};
    const dispatch = useAppDispatch();
    const { onShowAlert: onShowErrorAlert } =
    useContext(NetworkErrorAlertContext) || {};
    const { businessId } = useAppSelector(
        (state) => state.users,
    );

    const [data, setData] = useState({
       roles: null
    });

    const getAllBusinessRoles = async () => {
        const data = await dispatch(
            GetAllRoles({ businessId }),
        );
        if (GetAllRoles.rejected.match(data)) {
            onShowErrorAlert(
                "",
                String(
                    "An error occured when getting roles",
                ),
            );
        }
        if (GetAllRoles.fulfilled.match(data)) {
           
            setData((prev) => ({
                ...prev,
                roles: data.payload.data.business_roles,
            }));
        }

    };

    useEffect(() => {
        if(businessId){
            getAllBusinessRoles();
        }
    }, [businessId]);

    const handleCloseModal = () => {
        setState((prev) => ({
            ...prev,
            openModal: false,
        }));
        setValue("firstName", "");
        setValue("lastName", "");
        setValue("email", "");
        setValue("phoneNumber", null);
        setValue("role", null);
    };

    const mailformat =
        /^\w+([._+-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,})+$/;

    useEffect(() => {
        if (apiSuccess) {
            setValue("firstName", "");
            setValue("lastName", "");
            setValue("email", "");
            setValue("phoneNumber", null);
            setValue("role", null);


        }
    }, [apiSuccess]);

    const rolesOptions = data?.roles?.map(role => ({
        value: role.name,
        label: capitalizeName(role.name)
    }));
    
    
    return (
        <CustomModal
            isModalVisible={open}
            onClose={handleCloseModal}
            width="480px"
        >
            <InviteContent>
                <h1>Invite a team member</h1>

                <Flex>
                    <div>
                        <Controller
                            name="firstName"
                            rules={{ required: true }}
                            control={control}
                            render={({ field }) => (
                                <Input
                                    label="First name"
                                    disableLabel
                                    value={field.value}
                                    onChange={
                                        field.onChange
                                    }
                                    hasError={
                                        errors?.firstName
                                            ?.type ===
                                        "required"
                                    }
                                />
                            )}
                        />
                        {errors?.firstName?.type ===
                            "required" && (
                            <span className="error">
                                First name is required
                            </span>
                        )}
                    </div>

                    <div>
                        <Controller
                            name="lastName"
                            rules={{ required: true }}
                            control={control}
                            render={({ field }) => (
                                <Input
                                    label="Last name"
                                    disableLabel
                                    value={field.value}
                                    onChange={
                                        field.onChange
                                    }
                                    hasError={
                                        errors?.lastName
                                            ?.type ===
                                        "required"
                                    }
                                />
                            )}
                        />
                        {errors?.lastName?.type ===
                            "required" && (
                            <span className="error">
                                Last name is required
                            </span>
                        )}
                    </div>

                    <div>
                        <Controller
                            name="email"
                            rules={{
                                required: true,
                                pattern: mailformat,
                            }}
                            control={control}
                            render={({ field }) => (
                                <Input
                                    disableLabel
                                    label="Email"
                                    value={field.value}
                                    onChange={
                                        field.onChange
                                    }
                                    hasError={
                                        errors?.email
                                            ?.type ===
                                        "required"
                                    }
                                />
                            )}
                        />
                        {errors?.email?.type ===
                            "required" && (
                            <span className="error">
                                Email is required
                            </span>
                        )}
                        {errors.email?.type ===
                            "pattern" && (
                            <span className="error">
                                Please enter a valid email
                                address
                            </span>
                        )}
                    </div>

                    <div>
                        <Controller
                            name="phoneNumber"
                            rules={{
                                required: true,
                            }}
                            control={control}
                            render={({ field }) => (
                                <InputPhone
                                    label="Phone number"
                                    field={field}
                                    hasError={
                                        errors?.phoneNumber
                                            ?.type ===
                                        "required"
                                    }
                                    height="46px"
                                />
                            )}
                        />
                        {errors?.phoneNumber?.type ===
                            "required" && (
                            <span className="error">
                                Phone number is required
                            </span>
                        )}

                        {errors.phoneNumber?.type ===
                            "pattern" && (
                            <span className="error">
                                Please enter a valid phone
                                number
                            </span>
                        )}
                    </div>
                    <div>
                    <Controller
                            name="role"
                            rules={{
                                required: true,
                            }}
                            control={control}
                            render={({ field }) => (
                                <CustomSelect
                                    placeholder="Select a role"
                                    field={field}
                                    options={rolesOptions}
                                    $width="430px"
                                    lightBorder={true}
                                    $height="46px"
                                />
                            )}
                        />
                    {errors.role?.type ===
                            "required" && (
                            <span className="error">
                                Role is required
                            </span>
                        )}

                    </div>
                </Flex>

                <Note />
                   
                <Button
                    label="Invite team member"
                    height="40px"
                    onClick={handleSubmit(handleInviteTeam)}
                />
            </InviteContent>
        </CustomModal>
    );
};

export default InviteModal;
